import { safeSessionStorage } from "@/utils/safeStorage";
import React from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { registerServiceWorker } from "./registerServiceWorker";
import { initWebVitals } from "./lib/webVitals";
import { installErrorTelemetry } from "./lib/telemetry/errorTelemetry";
import { initThemeLab } from "./lib/themeLab";

registerServiceWorker();
initWebVitals();
installErrorTelemetry();
initThemeLab();

/**
 * Recover from stale dynamic-chunk imports (common on mobile after a deploy
 * when the service worker has cached an old index.html or chunk graph).
 * Detects ChunkLoadError / failed dynamic import and force-reloads once.
 */
const RELOAD_FLAG = "__gb_chunk_reload";
const isChunkError = (msg: unknown) => {
  const s = String(msg ?? "");
  return (
    s.includes("ChunkLoadError") ||
    s.includes("Loading chunk") ||
    s.includes("Failed to fetch dynamically imported module") ||
    s.includes("Importing a module script failed") ||
    s.includes("error loading dynamically imported module")
  );
};
const recoverFromChunkError = async () => {
  if (safeSessionStorage.getItem(RELOAD_FLAG)) return;
  safeSessionStorage.setItem(RELOAD_FLAG, "1");
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* ignore */
  }
  window.location.reload();
};
window.addEventListener("error", (e) => {
  if (isChunkError(e?.message) || isChunkError((e?.error as Error)?.message)) {
    recoverFromChunkError();
  }
});
window.addEventListener("unhandledrejection", (e) => {
  const reason = e?.reason;
  if (isChunkError(reason?.message) || isChunkError(reason)) {
    recoverFromChunkError();
  }
});

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>,
);
